import type { SubAttribute } from "~utils";

import { UnsyncedSubAttribute } from "./computeOnboardingState";
import type { AppearsWhen } from "./types/appearsWhen";

/**
 * Determines if a Step should appear based on `appearsWhen` criteria and
 * current sub attributes.
 *
 * Step Logic:
 * - Will BE visible when there IS NO matching onboarding attribute AND value
 * - Will BE visible when there IS A matching onboarding attribute AND
 *   value
 * - Will NOT BE visible when there IS a matching onboarding attribute AND
 *   NO matching value
 *
 * @example
 * // Step visible when there are IS NO matching attribute AND value
 * const appearsWhen = { "question-1": ["english"] };
 * const attributes3 = [{ attribute: "other-question", value: "value" }];
 * shouldStepAppear({ appearsWhen, syncedSubAttributes: attributes3 }); // true
 *
 * // Step visible when there IS A matching attribute AND the value
 * const appearsWhen = { "question-1": ["english"] };
 * const attributes = [{ attribute: "question-1", value: "english" }];
 * shouldStepAppear({ appearsWhen, syncedSubAttributes: attributes }); // true
 *
 * // Step NOT visible when there IS a matching attribute and NOT the value
 * const appearsWhen = { "question-1": ["english"] };
 * const attributes2 = [{ attribute: "question-1", value: "spanish" }];
 * shouldStepAppear({ appearsWhen, syncedSubAttributes: attributes2 }); // false
 *
 */
export function shouldStepAppear(params: {
  appearsWhen?: AppearsWhen;
  syncedSubAttributes?: Array<SubAttribute>;
  unsyncedSubAttributes?: Array<UnsyncedSubAttribute>;
}): boolean {
  const { appearsWhen, syncedSubAttributes = [], unsyncedSubAttributes = [] } = params;

  // If no appearsWhen defined, always show the step
  if (!appearsWhen) return true;

  // For each appearsWhen criteria
  return (
    Object.entries(appearsWhen)
      // If any criteria is not explicitly met, then hide the Step
      .every(([identifier, values]) => {
        const matchingSubAttribute = [...syncedSubAttributes, ...unsyncedSubAttributes].find(
          (subAttribute) => subAttribute.attribute === identifier,
        );

        // If there's a matching sub attribute with a value and it's NOT in
        // the allowed values array, hide the step
        if (
          matchingSubAttribute &&
          matchingSubAttribute.value &&
          !values!.includes(
            typeof matchingSubAttribute.value === "string"
              ? matchingSubAttribute.value
              : JSON.stringify(matchingSubAttribute.value),
          )
        ) {
          return false;
        }

        return true;
      })
  );
}

/**
 * Determines if a Task should appear based on `appearsWhen` criteria and
 * current sub attributes.
 *
 * Task Logic:
 * - Will NOT BE visible when there IS NO matching onboarding attribute
 * - Will BE visible when there IS a matching onboarding attribute
 * - Will NOT BE visible when there IS a matching onboarding attribute AND
 *   NO matching value
 *
 * @example
 * // Task IS NOT visible when there IS NO matching attribute AND value
 * const appearsWhen = { "question-1": ["english"] };
 * const attributes = [{ attribute: "other-question", value: "value" }];
 * shouldTaskAppear({ appearsWhen, syncedSubAttributes: attributes }); // false
 *
 * // Task IS visible when there IS A matching attribute AND value
 * const appearsWhen = { "question-1": ["english"] };
 * const attributes = [{ attribute: "question-1", value: "english" }];
 * shouldTaskAppear({ appearsWhen, syncedSubAttributes: attributes }); // true
 *
 * // Task IS NOT visible when there IS a matching attribute AND NOT the value
 * const appearsWhen = { "question-1": ["english"] };
 * const attributes2 = [{ attribute: "question-1", value: "spanish" }];
 * shouldTaskAppear({ appearsWhen, syncedSubAttributes: attributes2 }); // false
 */
export function shouldTaskAppear(params: {
  appearsWhen?: AppearsWhen;
  syncedSubAttributes?: Array<SubAttribute>;
  unsyncedSubAttributes?: Array<UnsyncedSubAttribute>;
}): boolean {
  const { appearsWhen, syncedSubAttributes = [], unsyncedSubAttributes = [] } = params;

  // If no appearsWhen defined, always show the Task
  if (!appearsWhen) return true;

  // For each appearsWhen criteria
  return (
    Object.entries(appearsWhen)
      // If any criteria is not explicitly met, then hide the Task
      .every(([identifier, values]) => {
        const matchingSubAttribute = [...syncedSubAttributes, ...unsyncedSubAttributes].find(
          (subAttribute) => subAttribute.attribute === identifier,
        );

        // Key difference from Step: If no matching attribute exists for this
        // identifier, hide the task
        if (!matchingSubAttribute) {
          return false;
        }

        // If there's a matching sub attribute with a value and it's NOT in
        // the allowed values array, hide the task
        if (
          matchingSubAttribute.value &&
          !values!.includes(
            typeof matchingSubAttribute.value === "string"
              ? matchingSubAttribute.value
              : JSON.stringify(matchingSubAttribute.value),
          )
        ) {
          return false;
        }

        return true;
      })
  );
}
